/**
 * Serviço para capturar e registrar erros do aplicativo
 * Registra erros no Firebase com informações contextuais
 */

import { db } from "../config/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

class ErrorLogService {
  constructor() {
    this.setupGlobalErrorHandlers();
  }

  /**
   * Registrar erro customizado
   * @param {Error|string} error - Erro ou mensagem
   * @param {object} context - Contexto adicional
   * @param {object} userInfo - Informações do usuário (opcional)
   */
  async logError(error, context = {}, userInfo = null) {
    try {
      // Verificar se o Firebase está inicializado de forma mais robusta
      if (!db || typeof db !== "object") {
        console.warn(
          "[ErrorLog] Firebase db não inicializado, salvando localmente"
        );
        this.saveErrorLocally(error, context);
        return false;
      }

      const errorData = this.createErrorData(error, context, userInfo);

      // Debug: Mostrar dados do usuário sendo capturados
      console.log("[ErrorLog] Dados do usuário capturados:", {
        userId: errorData.userId,
        userEmail: errorData.userEmail,
        userInfoProvided: !!userInfo,
        localStorage: this.debugLocalStorage(),
        sessionStorage: this.debugSessionStorage(),
      });

      // Registrar no Firebase com verificação adicional e delay para inicialização
      try {
        // Aguardar um pouco para garantir que o Firebase esteja pronto
        await new Promise((resolve) => setTimeout(resolve, 100));

        const errorLogsCollection = collection(db, "error_logs");
        await addDoc(errorLogsCollection, errorData);
      } catch (firestoreError) {
        console.error(
          "[ErrorLog] Erro específico do Firestore:",
          firestoreError
        );
        // Tentar salvar localmente como fallback
        this.saveErrorLocally(error, context);
        throw firestoreError;
      }

      // Também registrar no console para debug local
      console.error("[ErrorLog]", errorData);

      return true;
    } catch (logError) {
      // Se falhar ao registrar no Firebase, pelo menos registrar no console
      console.error("[ErrorLog] Falha ao registrar erro:", logError);
      console.error("[ErrorLog] Erro original:", error);

      // Se for problema de permissão, registrar localmente
      if (logError?.code === "permission-denied") {
        console.warn(
          "[ErrorLog] Problema de permissão - dados salvos localmente"
        );
        this.saveErrorLocally(error, context);
      }

      return false;
    }
  }

  /**
   * Salvar erro localmente quando Firebase falha
   */
  saveErrorLocally(error, context = {}) {
    try {
      const errorData = this.createErrorData(error, context);
      const localErrors = JSON.parse(
        localStorage.getItem("localErrors") || "[]"
      );
      localErrors.push(errorData);

      // Manter apenas os últimos 50 erros
      if (localErrors.length > 50) {
        localErrors.splice(0, localErrors.length - 50);
      }

      localStorage.setItem("localErrors", JSON.stringify(localErrors));
    } catch (e) {
      console.error("[ErrorLog] Falha ao salvar localmente:", e);
    }
  }

  /**
   * Remover campos undefined de um objeto (Firestore não aceita undefined)
   */
  removeUndefinedFields(obj) {
    const cleaned = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        if (
          typeof obj[key] === "object" &&
          obj[key] !== null &&
          !Array.isArray(obj[key])
        ) {
          // Recursivamente limpar objetos aninhados
          cleaned[key] = this.removeUndefinedFields(obj[key]);
        } else {
          cleaned[key] = obj[key];
        }
      }
    }
    return cleaned;
  }

  /**
   * Criar dados estruturados do erro
   */
  createErrorData(error, context = {}, userInfo = null) {
    const now = new Date();

    // Priorizar dados do usuário vindos do parâmetro
    let userId = context.userId;
    let userEmail = context.userEmail;

    if (userInfo) {
      userId = userId || userInfo.uid || userInfo.membroId || userInfo.userId;
      userEmail = userEmail || userInfo.email;
    }

    // Fallback para métodos automáticos
    userId = userId || this.getCurrentUserId();
    userEmail = userEmail || this.getCurrentUserEmail();

    const errorData = {
      // Informações do erro
      message: error?.message || String(error),
      stack: error?.stack || null,
      name: error?.name || "UnknownError",

      // Nível e tipo do erro
      level: context.level || this.determineLevel(error, context),
      type: context.type || this.determineType(error, context),

      // Contexto da aplicação
      component: context.component || "Unknown",
      action: context.action || "Unknown",
      userId: userId,
      userEmail: userEmail,

      // Informações do dispositivo/browser
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,

      // Informações da tela
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,

      // Informações da página
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer,

      // Timestamp
      timestamp: serverTimestamp(),
      localTimestamp: now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      // Informações adicionais do contexto
      ...context,

      // Detectar tipo de dispositivo
      isMobile:
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ),
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
      isAndroid: /Android/.test(navigator.userAgent),

      // Informações de suporte a recursos
      supportsCamera:
        "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices,
      supportsFileAPI:
        "File" in window &&
        "FileReader" in window &&
        "FileList" in window &&
        "Blob" in window,
      supportsServiceWorker: "serviceWorker" in navigator,
      supportsNotifications: "Notification" in window,
    };

    // Remover campos undefined antes de enviar ao Firestore
    return this.removeUndefinedFields(errorData);
  }

  /**
   * Determinar nível do erro baseado no contexto
   */
  determineLevel(error, context) {
    // Se já vem definido no contexto, usar
    if (context.level) return context.level;

    // Baseado na mensagem ou tipo
    const message = (error?.message || String(error)).toLowerCase();

    if (message.includes("sucesso") || message.includes("success")) {
      return "success";
    }

    if (message.includes("warn") || message.includes("aviso")) {
      return "warning";
    }

    if (
      message.includes("info") ||
      message.includes("tentando") ||
      message.includes("permissão")
    ) {
      return "info";
    }

    // Padrão é error
    return "error";
  }

  /**
   * Determinar tipo do erro baseado no componente e contexto
   */
  determineType(error, context) {
    // Se já vem definido no contexto, usar
    if (context.type) return context.type;

    const component = context.component || "";
    const action = context.action || "";
    const message = (error?.message || String(error)).toLowerCase();

    // Baseado no componente
    if (component === "CameraGallery") return "camera";
    if (component === "ResourceLoader") return "resource";
    if (component === "GlobalErrorHandler") return "javascript";
    if (component === "PWA") return "pwa";
    if (component === "Notifications") return "notification";

    // Baseado na mensagem
    if (message.includes("resource loading")) return "resource";
    if (message.includes("camera") || message.includes("câmera"))
      return "camera";
    if (message.includes("notification") || message.includes("notificação"))
      return "notification";
    if (message.includes("pwa") || message.includes("service worker"))
      return "pwa";
    if (message.includes("network") || message.includes("fetch"))
      return "network";

    return "unknown";
  }

  /**
   * Obter ID do usuário atual
   */
  getCurrentUserId() {
    try {
      // Tentar múltiplas fontes para obter o ID do usuário

      // 1. Tentar localStorage com diferentes chaves
      const possibleKeys = ["authUser", "churchUser", "currentUser", "user"];

      for (const key of possibleKeys) {
        const data = localStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            const userId =
              parsed.uid || parsed.id || parsed.membroId || parsed.userId;
            if (userId) return userId;
          } catch (e) {
            // Continuar tentando outras chaves
          }
        }
      }

      // 2. Tentar sessionStorage com as mesmas chaves
      for (const key of possibleKeys) {
        const data = sessionStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            const userId =
              parsed.uid || parsed.id || parsed.membroId || parsed.userId;
            if (userId) return userId;
          } catch (e) {
            // Continuar tentando outras chaves
          }
        }
      }

      // 3. Tentar acessar o contexto do Firebase Auth se disponível
      if (window.firebase && window.firebase.auth) {
        const currentUser = window.firebase.auth().currentUser;
        if (currentUser) {
          return currentUser.uid;
        }
      }

      return null;
    } catch (e) {
      console.warn("[ErrorLogService] Erro ao obter ID do usuário:", e);
      return null;
    }
  }

  /**
   * Obter email do usuário atual
   */
  getCurrentUserEmail() {
    try {
      // Tentar múltiplas fontes para obter o email do usuário

      // 1. Tentar localStorage com diferentes chaves
      const possibleKeys = ["authUser", "churchUser", "currentUser", "user"];

      for (const key of possibleKeys) {
        const data = localStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            const email = parsed.email || parsed.userEmail;
            if (email) return email;
          } catch (e) {
            // Continuar tentando outras chaves
          }
        }
      }

      // 2. Tentar sessionStorage com as mesmas chaves
      for (const key of possibleKeys) {
        const data = sessionStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            const email = parsed.email || parsed.userEmail;
            if (email) return email;
          } catch (e) {
            // Continuar tentando outras chaves
          }
        }
      }

      // 3. Tentar acessar o contexto do Firebase Auth se disponível
      if (window.firebase && window.firebase.auth) {
        const currentUser = window.firebase.auth().currentUser;
        if (currentUser) {
          return currentUser.email;
        }
      }

      return null;
    } catch (e) {
      console.warn("[ErrorLogService] Erro ao obter email do usuário:", e);
      return null;
    }
  }

  /**
   * Configurar handlers globais de erro
   */
  setupGlobalErrorHandlers() {
    // Capturar erros JavaScript não tratados
    window.addEventListener("error", (event) => {
      this.logError(event.error || new Error(event.message), {
        component: "GlobalErrorHandler",
        action: "UnhandledError",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        source: "window.error",
      });
    });

    // Capturar promises rejeitadas não tratadas
    window.addEventListener("unhandledrejection", (event) => {
      this.logError(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
        component: "GlobalErrorHandler",
        action: "UnhandledRejection",
        reason: event.reason,
        source: "unhandledrejection",
      });
    });

    // Capturar erros de recursos (imagens, scripts, etc.)
    window.addEventListener(
      "error",
      (event) => {
        if (event.target !== window) {
          const resourceUrl = event.target.src || event.target.href || "";

          // Lista de recursos externos a ignorar (não logar)
          const ignoredResources = [
            "googleusercontent.com", // Imagens do Google (avatar)
            "google.com/avatar", // Avatares do Google
            "lh3.googleusercontent.com", // Google User Content
            "gstatic.com", // Recursos estáticos do Google
          ];

          // Verificar se o recurso deve ser ignorado
          const shouldIgnore = ignoredResources.some((ignored) =>
            resourceUrl.includes(ignored)
          );

          if (shouldIgnore) {
            // Log silencioso no console (sem enviar ao Firebase)
            console.warn(
              "[ErrorLog] Erro de recurso externo ignorado:",
              resourceUrl
            );
            return;
          }

          // Logar outros erros de recursos
          this.logError(new Error(`Resource loading error: ${resourceUrl}`), {
            component: "ResourceLoader",
            action: "ResourceError",
            tagName: event.target.tagName,
            source: resourceUrl,
            resourceType: "resource",
          });
        }
      },
      true
    );
  }

  /**
   * Log específico para problemas de câmera/galeria
   */
  async logCameraError(error, context = {}) {
    return this.logError(error, {
      component: "CameraGallery",
      action: "PhotoAccess",
      type: "camera",
      level:
        context.level ||
        (error?.message?.includes("sucesso") ? "success" : "error"),
      supportsGetUserMedia:
        "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices,
      supportsCamera:
        "camera" in navigator ||
        ("mediaDevices" in navigator &&
          "getUserMedia" in navigator.mediaDevices),
      permissions: await this.checkPermissions(),
      ...context,
    });
  }

  /**
   * Verificar permissões disponíveis
   */
  async checkPermissions() {
    try {
      if ("permissions" in navigator) {
        const camera = await navigator.permissions.query({ name: "camera" });
        return {
          camera: camera.state,
        };
      }
      return { available: false };
    } catch (e) {
      return { error: e.message };
    }
  }

  /**
   * Log para problemas de PWA
   */
  async logPWAError(error, context = {}) {
    return this.logError(error, {
      component: "PWA",
      action: "PWAOperation",
      type: "pwa",
      level: context.level || "error",
      standalone: window.matchMedia("(display-mode: standalone)").matches,
      installed: window.navigator.standalone === true,
      serviceWorkerSupport: "serviceWorker" in navigator,
      ...context,
    });
  }

  /**
   * Log para problemas de notificação
   */
  async logNotificationError(error, context = {}) {
    return this.logError(error, {
      component: "Notifications",
      action: "NotificationOperation",
      type: "notification",
      level: context.level || "error",
      notificationSupport: "Notification" in window,
      permission:
        "Notification" in window ? Notification.permission : "not-supported",
      serviceWorkerSupport: "serviceWorker" in navigator,
      ...context,
    });
  }

  /**
   * Debug: Mostrar dados do localStorage
   */
  debugLocalStorage() {
    const data = {};
    const possibleKeys = ["authUser", "churchUser", "currentUser", "user"];

    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch (e) {
          data[key] = value;
        }
      }
    }

    return data;
  }

  /**
   * Debug: Mostrar dados do sessionStorage
   */
  debugSessionStorage() {
    const data = {};
    const possibleKeys = ["authUser", "churchUser", "currentUser", "user"];

    for (const key of possibleKeys) {
      const value = sessionStorage.getItem(key);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch (e) {
          data[key] = value;
        }
      }
    }

    return data;
  }

  /**
   * Criar um wrapper para funções que podem gerar erro
   */
  wrapWithErrorLogging(fn, context = {}) {
    return async (...args) => {
      try {
        return await fn(...args);
      } catch (error) {
        await this.logError(error, context);
        throw error; // Re-throw para manter o comportamento original
      }
    };
  }
}

// Instância global do serviço
const errorLogService = new ErrorLogService();

export default errorLogService;

// Exportações nomeadas para facilitar o uso
export const logError = (error, context = {}, userInfo = null) =>
  errorLogService.logError(error, context, userInfo);

export const logCameraError = (error, context = {}) =>
  errorLogService.logCameraError(error, context);

export const logPWAError = (error, context = {}) =>
  errorLogService.logPWAError(error, context);

export const logNotificationError = (error, context = {}) =>
  errorLogService.logNotificationError(error, context);

export const wrapWithErrorLogging = (fn, context = {}) =>
  errorLogService.wrapWithErrorLogging(fn, context);

// Hook para usar em componentes React
export const useErrorLogger = (userInfo = null) => {
  return {
    logError: (error, context = {}) =>
      errorLogService.logError(error, context, userInfo),
    logCameraError: (error, context = {}) =>
      errorLogService.logCameraError(error, { ...context, userInfo }),
    logPWAError: (error, context = {}) =>
      errorLogService.logPWAError(error, { ...context, userInfo }),
    logNotificationError: (error, context = {}) =>
      errorLogService.logNotificationError(error, { ...context, userInfo }),
    wrapWithErrorLogging: (fn, context = {}) =>
      errorLogService.wrapWithErrorLogging(fn, { ...context, userInfo }),
  };
};
